import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { TransitionGroup } from 'react-transition-group';
// @mui
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LoadingButton } from "@mui/lab";
import { DesktopDateTimePicker } from '@mui/x-date-pickers-pro';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import { TimeOffStepper } from './';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import AddPastTimeFiles from './AddPastTimeFiles';
import DIOText from '../../components/DIOText';
// utils
import { fKrTrackingTimeKST } from "../../utils/formatTime";
import axios from '../../utils/axios';
import {default as axiosNormal} from 'axios';


export default function AddPastTimeDialog({ isOpen, closeDialog, addCommute }) {
  const smUp = useResponsive('up', 'sm');
  const router = useRouter();
  const { c } = router.query;

  const [startAt, setStartAt] = useState(new Date());
  const [endAt, setEndAt] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [step, setStep] = useState(0); // [0, 1, 2] = [투두, 퇴근시간, 확인]

  const [timeOverlapError, setTimeOverlapError] = useState(false);
  const [timeOverlapItems, setTimeOverlapItems] = useState([]);

  // 메모
  const [memo, setMemo] = useState("");
  const [memoLength, setMemoLength] = useState(0);
  const [files, setFiles] = useState([]);

  const pastTime = (startAt && endAt) ? new Date(endAt).getTime() - new Date(startAt).getTime() : 0;
  const hours = Math.floor(pastTime / (1000 * 60 * 60));
  const minutes = Math.floor((pastTime % (1000 * 60 * 60)) / (1000 * 60));

  // submit value
  const finalPastTime = new Date(endAt).getTime() - new Date(startAt).getTime();
  const finalHours = Math.floor(finalPastTime / (1000 * 60 * 60));
  const finalMinutes = Math.floor((finalPastTime % (1000 * 60 * 60)) / (1000 * 60));

  const fileUploadBeforeSubmit = async (files) => {
    const fileNameList = [];
    for (let fileDict of files) {
      const f = fileDict.file;
      if (f.name === '' || f.name === null) {
        continue;
      } else {
        const urlResponse = await axios.post(
          `/api/works/time-tracking/crew/company/${c}/past/file-url/`,
          {filename: f.name}
        );
        const {filename, url} = urlResponse.data;

        // upload file to s3 via presigned url
        await axiosNormal.put(
          url,
          f,
          {
            headers: {
              'Content-Type': f.type
            }
          }
        );
        fileNameList.push({ file: filename, name: f.name });
      }
    }
    return fileNameList;
  };

  const submitTime = async () => {
    setIsSubmitting(true);
    const fileUploadedData = await fileUploadBeforeSubmit(files);
    addCommute({
      start_at: startAt.toISOString(),
      end_at: endAt.toISOString(),
      memo: memo || '',
      files: fileUploadedData,
    })
  };

  const goPrev = () => {
    if (step === 0) {
      return ;
    }
    setStep(step - 1);
  };

  const nextStep = async () => {
    if (step === 2) {
      submitTime();
      return ;
    }
    if (step === 0) {
      const isAvailable = await checkTimeAvailable();
      if (isAvailable) {
        setStep(step + 1);
      }
      return ;
    }
    setStep(step + 1);
  }

  const checkTimeAvailable = async () => {
    try {
      setIsSubmitting(true);
      const response = await axios.post(`/api/works/time-tracking/crew/company/${c}/past/available/`, {
        start_at: startAt.toISOString(),
        end_at: endAt.toISOString(),
      });
      setTimeOverlapError(!response.data?.available);
      setTimeOverlapItems(response.data?.overlap || []);
      setIsSubmitting(false);
      return response.data.available;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
      setStartAt(new Date());
      setEndAt(new Date());
      setCanSubmit(true);
      setStep(0);
      setMemo("");
      setMemoLength(0);
      setTimeOverlapItems([]);
      setTimeOverlapError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setCanSubmit((endAt ? endAt <= new Date() : false) && (startAt ? startAt <= new Date() : false) && (startAt < endAt));
  }, [startAt, endAt]);

  const handleMemoChange = (e) => {
    setMemo(e.target.value);
    setMemoLength(e.target.value.length);
  };

  useEffect(() => {
    if (step === 1) {
      setCanSubmit(memo.length >= 20);
    }
  }, [step, memo]);

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm" fullScreen={!smUp}>
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
        {/* header */}
        <Stack direction="row" sx={{ justifyContent: "space-between", mb: { xs: 2, sm: 4 } }} alignItems="center">
          <IconButton color="inherit" edge="start" onClick={goPrev} sx={{ ...(step === 0 && { visibility: "hidden" })}}>
            <Iconify icon="ic:chevron-left" width={32} height={32} />
          </IconButton>
          <TimeOffStepper steps={["투두", "퇴근시간", "확인"]} step={step} />
          {/* padding */}
          <IconButton color="inherit" edge="end" onClick={closeDialog} sx={{ visibility: "hidden" }}>
            <Iconify icon="ic:close" width={24} height={24} />
          </IconButton>
        </Stack>

        {/* step 0 */}
        {step === 0 && (
        <>
          <DocumentLabel />
          <Stack spacing={1} sx={{ mb: 4 }}>
            <Typography variant={smUp ? "h4" : "h5"}>
              출퇴근 시간 수동 등록하기
            </Typography>
            {timeOverlapError && timeOverlapItems?.length > 0 && (
              <Stack
                direction="row" alignItems={"flex-start"} spacing={1}
                sx={{ px: 2, py: 1.5, border: "1px solid", borderColor: "error.main", borderRadius: 1 }}
              >
                <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'error.main' }} />
                <Stack spacing={.5}>
                  <Typography variant={"subtitle1"} sx={{ color: "error.darker" }}>
                    이미 등록된 출퇴근 시간과 겹치지 않게 등록해주세요. 
                  </Typography>
                  <Typography variant={"body2"} sx={{ color: "error.darker" }}>
                    {fKrTrackingTimeKST(timeOverlapItems[0].work_start)} ~ {fKrTrackingTimeKST(timeOverlapItems[0].work_end)}
                    {timeOverlapItems.length > 1 && ` 외 ${timeOverlapItems.length - 1}건`}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>

          <Typography variant="subtitle1" paragraph>
            출퇴근 시간 (현지 시간 기준)
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
            <DesktopDateTimePicker
              value={startAt}
              maxDateTime={new Date()}
              onChange={(newValue) => setStartAt(newValue)}
              renderInput={(params) => (
                <TextField {...params} fullWidth helperText="과거 시간만 입력할 수 있습니다." />
              )}
              disableMaskedInput
            />
            <DesktopDateTimePicker
              value={endAt}
              maxDateTime={new Date()}
              onChange={(newValue) => setEndAt(newValue)}
              renderInput={(params) => (
                <TextField {...params} fullWidth />
              )}
              disableMaskedInput
            />
          </Stack>

          <Stack
            sx={{ px: 3, py: 2, backgroundColor: "grey.200", borderRadius: 2 }}
            direction="row" justifyContent="space-between"
          >
            <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
              총 근무 시간
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 20, height: 20, color: "text.secondary" }} />
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                {hours > 0 ? `${hours}시간` : ""} {minutes}분
              </Typography>
            </Stack>
          </Stack>
        </>
        )}
        {/* step 1 */}
        {step === 1 && (
        <>
          <DocumentLabel />
          <Typography variant={smUp ? "h4" : "h5"} gutterBottom>
            해당 시간에 진행했던 업무를 알려주세요.
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all", mb: { xs: 2, sm: 3 } }}>
            업무 내용을 증명하는 추가 자료가 있다면 첨부해주셔도 됩니다.
          </Typography>
          <Stack
            spacing={.5}
            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2.5 }, backgroundColor: "grey.200", borderRadius: 2, mb: 3 }}
            direction={{xs : "column", sm: "row" }} justifyContent="space-between"
          >
            <Typography variant={smUp ? "subtitle1" : "overline" } sx={{ color: "text.secondary" }}>
              {fKrTrackingTimeKST(startAt)} ~ {fKrTrackingTimeKST(endAt)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: smUp ? 20 : 16, height: smUp ? 20 : 16, color: "text.secondary" }} />
              <Typography variant={smUp ? "subtitle1" : "overline" } sx={{ color: "text.secondary" }}>
                {hours > 0 ? `${hours}시간` : ""} {minutes}분
              </Typography>
            </Stack>
          </Stack>
          <TextField
            id={"workEndMemo"}
            fullWidth
            placeholder={"(1) 해당 시간에 진행한 업무와 (2) 출퇴근 시간을 수동으로 등록하는 사유를 작성해 주세요."}
            multiline
            rows={6}
            value={memo}
            onChange={handleMemoChange}
          />
          <Box sx={{ width: 1, textAlign: "end" }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {memoLength === 0 && "최소 20자 이상"}
              {memoLength > 0 && `${memoLength}자/최소 20자`}
            </Typography>
          </Box>
          <AddPastTimeFiles files={files} setFiles={setFiles} />
        </>
        )}
        {/* step 2 */}
        {step === 2 && (
        <>
          <Typography variant={smUp ? "h4" : "h5"} gutterBottom>
            수동으로 등록한 출퇴근 시간을 승인 요청할까요?
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }} gutterBottom>
            기업과 <DIOText />에 승인 요청서가 발송됩니다. 기업과 <DIOText />가 검토 후, 자료가 부족하면 다시 요구할 수 있습니다.
          </Typography>

          <Box
            sx={{
              p: 3,
              my: 2,
              backgroundColor: "grey.200",
              borderRadius: 2,
            }}
          >
            <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", mb: 0.5 }}>
              총 근무 시간
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 20, height: 20, color: "text.secondary" }} />
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                {finalHours}시간 {finalMinutes}분
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} sx={{ mt: 3 }} spacing={smUp ? 2 : 1.5}>
              <Stack spacing={.5}
                sx={{ flexGrow: 1 }}
              >
                <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                  출근 시간
                </Typography>
                <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                  {fKrTrackingTimeKST(startAt)}
                </Typography>
              </Stack>
              {smUp && (
              <Stack spacing={.5}>
                <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", visibility: "hidden" }}>
                  -
                </Typography>
                <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                  ~
                </Typography>
              </Stack>
              )}
              <Stack spacing={.5}
                sx={{ flexGrow: 1 }}
              >
                <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                  퇴근 시간
                </Typography>
                <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                  {fKrTrackingTimeKST(endAt)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              p: 3,
              mt: 2,
              backgroundColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            <Stack spacing={1}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
                메모
              </Typography>
              <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }} variant={smUp ? "body1" : "body2"}>
                {memo}
              </Typography>
              {files?.length > 0 && files.map((item, index) => (
                <Typography variant="subtitle1" key={index} sx={{ color: "text.secondary", textDecoration: "underline" }}>
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>

        </>
        )}
      </DialogContent>
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack sx={{ width: 1 }} spacing={{ xs: 2, sm: 1 }}>
          <Stack
            justifyContent={"flex-end"}
            direction={smUp ? "row" : "column-reverse"}
            sx={{ width: 1 }}
            spacing={{ xs: 1, sm: 3 }}
          >
            <LoadingButton
              loading={isSubmitting}
              variant="text"
              onClick={closeDialog}
              size="large"
              sx={{ px: 6 }}
              fullWidth={!smUp}
            >
              취소
            </LoadingButton>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              onClick={nextStep}
              size="large"
              sx={{ px: 6 }}
              fullWidth={!smUp}
              disabled={!canSubmit}
            >
              {step === 2 ? "승인 요청하기" : "다음"}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  )
};

function DocumentLabel() {
  return (
    <Stack color="info" direction="row" spacing={1} alignItems="center"
      sx={{
        width: "fit-content",
        py: 0.5,
        px: 1,
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
        mb: 0.5,
      }}
    >
      <SvgIconStyle src={`/icons/icon-document-mono.svg`} sx={{ width: 14, height: 14, color: "info.main" }} />
      <Typography variant="subtitle2" sx={{ color: "info.dark" }}>
        출퇴근 등록 승인 요청서
      </Typography>
    </Stack>
  )
}
